import moment from 'moment';
import { api } from './request';


/**
 * Poll a report's availableForDownload endpoint and trigger a CSV download when ready.
 * Used by template-download flows so the frontend doesn't just show a static modal.
 *
 * @param reportId - the CompanyReport id to poll for
 * @param fileName - filename to use for the downloaded blob
 * @param maxSeconds - max seconds to poll before giving up (default 1800 = 30 min)
 * @param onProgress - optional callback receiving secondsRemaining and ready boolean each tick
 */
export async function pollReportAndDownload(
    reportId: number,
    fileName: string,
    maxSeconds = 1800,
    onProgress?: (secondsRemaining: number, ready: boolean, failed: boolean) => void,
): Promise<{ready: boolean, failed: boolean}> {
    return new Promise((resolve) => {
        let secondsLeft = maxSeconds;
        const tick = async () => {
            if (secondsLeft <= 0) {
                onProgress?.(0, false, false);
                resolve({ready: false, failed: false});
                return;
            }
            try {
                const res = await api.get(`/reports/${reportId}/availableForDownload`);
                if (res.data?.failed) {
                    onProgress?.(secondsLeft, false, true);
                    resolve({ready: false, failed: true});
                    return;
                }
                if (res.data?.available) {
                    try {
                        const downloadRes = await api.get(`/reports/${reportId}/download`, {responseType: 'blob'});
                        const url = window.URL.createObjectURL(new Blob([downloadRes.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', fileName);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                        onProgress?.(secondsLeft, true, false);
                        resolve({ready: true, failed: false});
                        return;
                    } catch {
                        onProgress?.(secondsLeft, false, true);
                        resolve({ready: false, failed: true});
                        return;
                    }
                }
            } catch {
                // Transient errors: keep polling
            }
            secondsLeft -= 1;
            onProgress?.(secondsLeft, false, false);
            setTimeout(tick, 1000);
        };
        tick();
    });
}



export function formatDate(
    dateToFormat: null | undefined | Date,
    withTime = false,
    withMilliseconds = false,
    customFormat: string | undefined = undefined
): string {

    if (dateToFormat === null || !dateToFormat) {
        return '-';
    }

    const date = moment(dateToFormat).local();

    if (customFormat) {
        return date.format(customFormat);
    }

    if (!withTime) {
        return date.format('DD.MM.YYYY');
    }

    if (withMilliseconds) {
        return moment(dateToFormat).local().format('DD.MM.YYYY HH:mm:ss.SSS');
    }

    return date.format('DD.MM.YYYY HH:mm');
}

export function formatNumber(number: null | undefined | string | number, round=false) {

    if (number === undefined || number === null) {
        return '0';
    }

    if (round) {
        number = (Math.round((typeof number === 'string' ? parseFloat(number) : number) * 100) / 100).toFixed(2);
    }

    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function formatDateForBackend(date: null | moment.Moment) {
    if (date === null) {
        return null;
    }

    return date.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
}

export const getSignupStepUrlFromKycStatus = (kycStatus: string) => {
    switch(kycStatus) {
        //TODO: michael: add history or do it another way
        case 'general':
            return '/signup/representatives';
        case 'representatives':
            return '/signup/owners';
        case 'owners':
            return '/signup/true_beneficiary';
        case 'true_beneficiary':
            return '/signup/political_importance';
        case 'political_importance':
            return '/signup/financial_information';
        case 'filled':
            return '/dashboard';
        case 'not_filled':
        default:
            return '/signup/general';
    }
}
